import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useParams,
} from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { lazy, Suspense, type ReactNode } from 'react';
import { Toaster } from 'sonner';
import { AppShell } from './shell/AppShell.js';
import { MessageList } from './features/mail-list/MessageList.js';
import {
  MailWorkspace,
  useEffectiveMailLayout,
} from './features/mail-view/MailWorkspace.js';

const LoginPage = lazy(() =>
  import('./pages/Login.js').then((module) => ({ default: module.LoginPage })),
);
const InvitePage = lazy(() =>
  import('./pages/InvitePage.js').then((module) => ({ default: module.InvitePage })),
);
const OfflineMailPage = lazy(() =>
  import('./pages/OfflineMailPage.js').then((module) => ({
    default: module.OfflineMailPage,
  })),
);
const MailRecoveryPage = lazy(() =>
  import('./pages/MailRecoveryPage.js').then((module) => ({
    default: module.MailRecoveryPage,
  })),
);
const CalendarPage = lazy(() =>
  import('./pages/CalendarPage.js').then((module) => ({ default: module.CalendarPage })),
);
const ContactsPage = lazy(() =>
  import('./pages/ContactsPage.js').then((module) => ({ default: module.ContactsPage })),
);
const PlannerPage = lazy(() =>
  import('./pages/PlannerPage.js').then((module) => ({ default: module.PlannerPage })),
);
const HelpPage = lazy(() =>
  import('./pages/HelpPage.js').then((module) => ({ default: module.HelpPage })),
);
const AccountsSettings = lazy(() =>
  import('./pages/settings/AccountsSettings.js').then((module) => ({
    default: module.AccountsSettings,
  })),
);
const AutomationSettings = lazy(() =>
  import('./pages/settings/AutomationSettings.js').then((module) => ({
    default: module.AutomationSettings,
  })),
);
const AiSettings = lazy(() =>
  import('./pages/settings/AiSettings.js').then((module) => ({ default: module.AiSettings })),
);
const CalendarSettings = lazy(() =>
  import('./pages/settings/CalendarSettings.js').then((module) => ({
    default: module.CalendarSettings,
  })),
);
const DefaultsSettings = lazy(() =>
  import('./pages/settings/DefaultsSettings.js').then((module) => ({
    default: module.DefaultsSettings,
  })),
);
const SignaturesSettings = lazy(() =>
  import('./pages/settings/SignaturesSettings.js').then((module) => ({
    default: module.SignaturesSettings,
  })),
);
const ComposeSettings = lazy(() => import('./pages/settings/ComposeSettings.js').then((module) => ({ default: module.ComposeSettings })));
const MailRulesSettings = lazy(() =>
  import('./pages/settings/MailRulesSettings.js').then((module) => ({
    default: module.MailRulesSettings,
  })),
);
const SecuritySettings = lazy(() =>
  import('./pages/settings/SecuritySettings.js').then((module) => ({
    default: module.SecuritySettings,
  })),
);
const NotificationSettings = lazy(() =>
  import('./pages/settings/NotificationSettings.js').then((module) => ({
    default: module.NotificationSettings,
  })),
);
const OfflineSettings = lazy(() =>
  import('./pages/settings/OfflineSettings.js').then((module) => ({
    default: module.OfflineSettings,
  })),
);
const StorageSettings = lazy(() =>
  import('./pages/settings/StorageSettings.js').then((module) => ({
    default: module.StorageSettings,
  })),
);
const SyncActivity = lazy(() =>
  import('./pages/settings/SyncActivity.js').then((module) => ({
    default: module.SyncActivity,
  })),
);
const UsersSettings = lazy(() =>
  import('./pages/settings/UsersSettings.js').then((module) => ({
    default: module.UsersSettings,
  })),
);
const ThreadView = lazy(() =>
  import('./features/thread/ThreadView.js').then((module) => ({
    default: module.ThreadView,
  })),
);

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <Toaster position="bottom-left" />
    </>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => (
    <RouteChunk>
      <LoginPage />
    </RouteChunk>
  ),
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: () => (
    <RouteChunk>
      <InvitePage />
    </RouteChunk>
  ),
});

const offlineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offline',
  component: () => (
    <RouteChunk>
      <OfflineMailPage />
    </RouteChunk>
  ),
});

const shellRoute = createRoute({
  id: 'shell',
  getParentRoute: () => rootRoute,
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/mail/unified' });
  },
});

// ---- unified inbox (thread routes are children so the list stays warm) ----
const unifiedRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/mail/unified',
  component: UnifiedMailWorkspace,
});
const unifiedIndexRoute = createRoute({
  getParentRoute: () => unifiedRoute,
  path: '/',
  component: () => <MessageList scope="unified" />,
});
const unifiedThreadRoute = createRoute({
  getParentRoute: () => unifiedRoute,
  path: 'thread/$threadId',
  component: ThreadPage,
});

// ---- server-timed snoozed conversations ----
const snoozedRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/mail/snoozed',
  component: SnoozedMailWorkspace,
});
const snoozedIndexRoute = createRoute({
  getParentRoute: () => snoozedRoute,
  path: '/',
  component: () => <MessageList scope="snoozed" />,
});
const snoozedThreadRoute = createRoute({
  getParentRoute: () => snoozedRoute,
  path: 'thread/$threadId',
  component: ThreadPage,
});

const mailRecoveryRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/mail/recovery',
  component: () => (
    <RouteChunk>
      <MailRecoveryPage />
    </RouteChunk>
  ),
});

// ---- single account/folder ----
const folderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/mail/a/$accountId/f/$folderId',
  component: FolderMailWorkspace,
});
const folderIndexRoute = createRoute({
  getParentRoute: () => folderRoute,
  path: '/',
  component: FolderList,
});
const folderThreadRoute = createRoute({
  getParentRoute: () => folderRoute,
  path: 'thread/$threadId',
  component: ThreadPage,
});

// ---- full-history search ----
const searchRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/mail/search',
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === 'string' ? search.q : '',
    sort: search.sort === 'relevance' ? ('relevance' as const) : ('newest' as const),
  }),
  component: SearchMailWorkspace,
});
const searchIndexRoute = createRoute({
  getParentRoute: () => searchRoute,
  path: '/',
  component: SearchList,
});
const searchThreadRoute = createRoute({
  getParentRoute: () => searchRoute,
  path: 'thread/$threadId',
  component: SearchThreadPage,
});

const calendarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/calendar',
  validateSearch: (search: Record<string, unknown>): CalendarRouteSearch => {
    const eventId = positiveSearchId(search.eventId);
    const recurrenceId = calendarRecurrenceSearchId(search.recurrenceId);
    return eventId === undefined
      ? {}
      : { eventId, ...(recurrenceId ? { recurrenceId } : {}) };
  },
  component: () => (
    <RouteChunk>
      <CalendarPage />
    </RouteChunk>
  ),
});

const contactsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/contacts',
  validateSearch: (search: Record<string, unknown>): ContactsRouteSearch => {
    const contactId = positiveSearchId(search.contactId);
    return contactId === undefined ? {} : { contactId };
  },
  component: () => (
    <RouteChunk>
      <ContactsPage />
    </RouteChunk>
  ),
});

const plannerRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/planner',
  validateSearch: (search: Record<string, unknown>): PlannerRouteSearch => ({
    ...(search.tab === 'notes' || search.tab === 'tasks'
      ? { tab: search.tab }
      : {}),
    ...(search.new === 'task' || search.new === 'note'
      ? { new: search.new }
      : {}),
    ...(positiveSearchId(search.threadId) === undefined
      ? {}
      : { threadId: positiveSearchId(search.threadId) }),
    ...(positiveSearchId(search.contactId) === undefined
      ? {}
      : { contactId: positiveSearchId(search.contactId) }),
    ...(positiveSearchId(search.eventId) === undefined
      ? {}
      : { eventId: positiveSearchId(search.eventId) }),
    ...(positiveSearchId(search.taskId) === undefined
      ? {}
      : { taskId: positiveSearchId(search.taskId) }),
    ...(positiveSearchId(search.noteId) === undefined
      ? {}
      : { noteId: positiveSearchId(search.noteId) }),
  }),
  component: () => (
    <RouteChunk>
      <PlannerPage />
    </RouteChunk>
  ),
});

const helpRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/help',
  validateSearch: (search: Record<string, unknown>): HelpRouteSearch => ({
    ...(typeof search.topic === 'string' && /^[a-z0-9-]{1,80}$/.test(search.topic)
      ? { topic: search.topic }
      : {}),
    ...(typeof search.q === 'string' && search.q.trim().length > 0
      ? { q: search.q.slice(0, 200) }
      : {}),
  }),
  component: HelpRoutePage,
});

const settingsAccountsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/accounts',
  component: () => (
    <RouteChunk>
      <AccountsSettings />
    </RouteChunk>
  ),
});

const settingsDefaultsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/defaults',
  component: () => (
    <RouteChunk>
      <DefaultsSettings />
    </RouteChunk>
  ),
});

const settingsSignaturesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/signatures',
  component: () => (
    <RouteChunk>
      <SignaturesSettings />
    </RouteChunk>
  ),
});
const settingsComposeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/compose',
  component: () => <RouteChunk><ComposeSettings /></RouteChunk>,
});

const settingsRulesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/rules',
  component: () => (
    <RouteChunk>
      <MailRulesSettings />
    </RouteChunk>
  ),
});

const settingsCalendarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/calendar',
  component: () => (
    <RouteChunk>
      <CalendarSettings />
    </RouteChunk>
  ),
});

const settingsSecurityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/security',
  component: () => (
    <RouteChunk>
      <SecuritySettings />
    </RouteChunk>
  ),
});

const settingsNotificationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/notifications',
  component: () => (
    <RouteChunk>
      <NotificationSettings />
    </RouteChunk>
  ),
});

const settingsOfflineRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/offline',
  component: () => (
    <RouteChunk>
      <OfflineSettings />
    </RouteChunk>
  ),
});

const settingsActivityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/activity',
  component: () => (
    <RouteChunk>
      <SyncActivity />
    </RouteChunk>
  ),
});

const settingsStorageRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/storage',
  component: () => (
    <RouteChunk>
      <StorageSettings />
    </RouteChunk>
  ),
});

const settingsAutomationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/automation',
  component: () => (
    <RouteChunk>
      <AutomationSettings />
    </RouteChunk>
  ),
});

const settingsAiRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/ai',
  component: () => (
    <RouteChunk>
      <AiSettings />
    </RouteChunk>
  ),
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/users',
  component: () => (
    <RouteChunk>
      <UsersSettings />
    </RouteChunk>
  ),
});

function FolderList() {
  const { accountId, folderId } = folderRoute.useParams();
  return (
    <MessageList
      key={`${accountId}:${folderId}`}
      scope={{ accountId: Number(accountId), folderId: Number(folderId) }}
    />
  );
}

function UnifiedMailWorkspace() {
  const layout = useEffectiveMailLayout();
  const { threadId } = useParams({ strict: false });
  if (layout === 'full') return <Outlet />;
  const previewThreadId = threadId ? Number(threadId) : undefined;
  return (
    <MailWorkspace
      layout={layout}
      list={<MessageList scope="unified" previewThreadId={previewThreadId} />}
      detail={previewThreadId ? <Outlet /> : null}
    />
  );
}

function SnoozedMailWorkspace() {
  const layout = useEffectiveMailLayout();
  const { threadId } = useParams({ strict: false });
  if (layout === 'full') return <Outlet />;
  const previewThreadId = threadId ? Number(threadId) : undefined;
  return (
    <MailWorkspace
      layout={layout}
      list={<MessageList scope="snoozed" previewThreadId={previewThreadId} />}
      detail={previewThreadId ? <Outlet /> : null}
    />
  );
}

function FolderMailWorkspace() {
  const layout = useEffectiveMailLayout();
  const { accountId, folderId, threadId } = useParams({ strict: false });
  if (layout === 'full') return <Outlet />;
  const previewThreadId = threadId ? Number(threadId) : undefined;
  return (
    <MailWorkspace
      layout={layout}
      list={
        <MessageList
          key={`${accountId}:${folderId}`}
          scope={{ accountId: Number(accountId), folderId: Number(folderId) }}
          previewThreadId={previewThreadId}
        />
      }
      detail={previewThreadId ? <Outlet /> : null}
    />
  );
}

function SearchMailWorkspace() {
  const layout = useEffectiveMailLayout();
  const { threadId } = useParams({ strict: false });
  const { q, sort } = searchRoute.useSearch();
  if (layout === 'full') return <Outlet />;
  const previewThreadId = threadId ? Number(threadId) : undefined;
  return (
    <MailWorkspace
      layout={layout}
      list={
        <MessageList
          key={`${q}:${sort}`}
          searchQuery={q}
          searchSort={sort}
          previewThreadId={previewThreadId}
        />
      }
      detail={previewThreadId ? <Outlet /> : null}
    />
  );
}

function ThreadPage() {
  const params = useParams({ strict: false });
  return (
    <RouteChunk>
      <ThreadView key={params.threadId} threadId={Number(params.threadId)} />
    </RouteChunk>
  );
}

function SearchList() {
  const { q, sort } = searchRoute.useSearch();
  return <MessageList key={`${q}:${sort}`} searchQuery={q} searchSort={sort} />;
}

function SearchThreadPage() {
  const { threadId } = searchThreadRoute.useParams();
  const { q, sort } = searchRoute.useSearch();
  return (
    <RouteChunk>
      <ThreadView
        key={`${threadId}:${q}:${sort}`}
        threadId={Number(threadId)}
        searchQuery={q}
        searchSort={sort}
      />
    </RouteChunk>
  );
}

function HelpRoutePage() {
  const { topic, q } = helpRoute.useSearch();
  return (
    <RouteChunk>
      <HelpPage topicSlug={topic} initialQuery={q} />
    </RouteChunk>
  );
}

function RouteChunk({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteLoading />}>{children}</Suspense>;
}

function RouteLoading() {
  return (
    <div
      className="flex h-full min-h-40 items-center justify-center gap-2 text-sm text-ink-muted"
      role="status"
    >
      <Loader2 className="size-5 animate-spin" />
      Loading…
    </div>
  );
}

const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  offlineRoute,
  shellRoute.addChildren([
    indexRoute,
    unifiedRoute.addChildren([unifiedIndexRoute, unifiedThreadRoute]),
    snoozedRoute.addChildren([snoozedIndexRoute, snoozedThreadRoute]),
    mailRecoveryRoute,
    folderRoute.addChildren([folderIndexRoute, folderThreadRoute]),
    searchRoute.addChildren([searchIndexRoute, searchThreadRoute]),
    calendarRoute,
    contactsRoute,
    plannerRoute,
    helpRoute,
    settingsAccountsRoute,
    settingsDefaultsRoute,
    settingsSignaturesRoute,
    settingsComposeRoute,
    settingsRulesRoute,
    settingsCalendarRoute,
    settingsActivityRoute,
    settingsAutomationRoute,
    settingsAiRoute,
    settingsSecurityRoute,
    settingsNotificationsRoute,
    settingsOfflineRoute,
    settingsStorageRoute,
    settingsUsersRoute,
  ]),
]);

export const router = createRouter({ routeTree });

interface CalendarRouteSearch {
  eventId?: number;
  recurrenceId?: string;
}

interface ContactsRouteSearch {
  contactId?: number;
}

interface PlannerRouteSearch {
  tab?: 'tasks' | 'notes';
  new?: 'task' | 'note';
  threadId?: number;
  contactId?: number;
  eventId?: number;
  taskId?: number;
  noteId?: number;
}

interface HelpRouteSearch {
  topic?: string;
  q?: string;
}

function positiveSearchId(value: unknown): number | undefined {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
        ? Number(value)
        : Number.NaN;
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function calendarRecurrenceSearchId(value: unknown): string | undefined {
  return typeof value === 'string' &&
    value.length <= 100 &&
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}Z?)?$/.test(value)
    ? value
    : undefined;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
